import React from "react";
import { CheckCircle, WarningCircle, ArrowRight } from "@phosphor-icons/react";
import { CATEGORY_ORDER, scoreTone } from "../../lib/clarity";

export const MEASURES = {
  cash: "How much of what you earn is put to work, and your cushion for the unexpected.",
  tax: "How savings are spread across taxable, tax-deferred, and tax-free categories — and whether withdrawal taxes are planned for.",
  protection: "Whether your protection matches the people and obligations that depend on you.",
  networth: "What you own versus what you owe, and which direction it is moving.",
  retirement: "Whether your savings pace and plan line up with your timeline.",
};

function CategoryCard({ c, note, i }) {
  return (
    <div data-testid={`report-category-${c.key}`} className={`card-surface p-6 sm:p-8 rise rise-${Math.min(i + 1, 4)}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="eyebrow text-exodus-muted">{c.weight}% of score</p>
          <h3 className="font-display text-2xl mt-1">{c.label}</h3>
        </div>
        <p data-testid={`report-category-${c.key}-score`} className={`font-display text-4xl leading-none ${scoreTone(c.score)}`}>{c.score ?? "—"}</p>
      </div>
      <div className="h-1 bg-exodus-border mt-4"><div className="h-1 bg-exodus-gold" style={{ width: `${c.score || 0}%`, transition: "width 1s ease-out" }} /></div>
      <p className="text-xs text-exodus-muted mt-4">{MEASURES[c.key]}</p>
      {c.score == null && <p className="text-sm text-exodus-muted mt-4">Not enough information to score this area yet.</p>}
      {c.confidence < 1 && c.score != null && <p className="text-[11px] text-exodus-muted mt-2">{c.answered} of {c.applicable} inputs answered — provisional.</p>}
      {note && <p className="text-sm text-exodus-text/90 mt-4 italic">{note}</p>}
      <div className="mt-5 space-y-4 text-sm">
        {c.strength && <p className="flex gap-2"><CheckCircle size={18} weight="fill" className="text-exodus-emerald shrink-0" /><span>{c.strength}</span></p>}
        {c.gap && (
          <div className="flex gap-2">
            <WarningCircle size={18} weight="fill" className="text-orange-300 shrink-0" />
            <div className="space-y-1">
              <p className="text-exodus-text">{c.gap}</p>
              <p className="text-exodus-muted text-xs"><span className="eyebrow text-[10px] mr-1">Why it matters</span>{c.why}</p>
              <p className="text-exodus-text/90 text-xs flex gap-1 pt-1"><ArrowRight size={12} className="mt-0.5 text-exodus-gold shrink-0" />{c.action}</p>
            </div>
          </div>
        )}
        {!c.gap && c.score != null && <p className="text-exodus-muted text-xs">No significant gap identified from your answers.</p>}
      </div>
    </div>
  );
}

export default function ReportCategories({ categories, narrative }) {
  return (
    <section data-testid="report-categories" className="grid md:grid-cols-2 gap-5">
      {CATEGORY_ORDER.map((k, i) => <CategoryCard key={k} c={categories[k]} note={narrative?.categories?.[k]} i={i} />)}
    </section>
  );
}
